'use client';

import type { ClassValue } from 'clsx';
import type { MouseEvent } from 'react';
import type { StockInfo } from '../../../product/index';
import { Button } from '@vs-shop/ui/button';
import { clsxm } from '@vs-shop/utils';
import { shouldAddToCartButtonBeDisabled } from '../../../shared/utils/stockValidation';
import { useIntl } from 'react-intl';

import { purchaseButtonClassName } from '../../../config/purchaseButtonClass';

import { IconCartOutline } from '@vs-shop/ui/icons';

import { useAddToCart, useVariantInCart } from '../../../hook/useAddToCart';
import { useCartLinePreviewPreload, withCartLinePreview } from '../../../hook/cartLinePreview';
import type { ProductAgeRestriction } from '@vs-shop/schemas/branded/ProductSchema';
import type { ProductImagePreview } from '../../../product/productImagePreview';

type Props = {
  productCode: string;
  productName: string;
  imagePreview: ProductImagePreview;
  ageRestriction: ProductAgeRestriction;
  price: number;
  priceWithoutTax: number;
  stock: StockInfo;
  className?: ClassValue;
};

export default function ProductCardRecommendationAddToCart({
  productCode,
  productName,
  imagePreview,
  ageRestriction,
  price,
  priceWithoutTax,
  stock,
  className,
}: Props) {
  const intl = useIntl();
  const addToCart = useAddToCart();
  const { quantityInCart, isAdding } = useVariantInCart(productCode);
  const imagePath = imagePreview.kind === 'available' ? imagePreview.template : null;
  const request = withCartLinePreview(
    {
      product: { code: productCode, name: productName, price, priceWithoutTax, stock },
      quantity: 1,
    },
    imagePath,
    ageRestriction
  );
  const preloadHandlers = useCartLinePreviewPreload([request.preview]);

  const onAddProductToCart = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    addToCart([request]);
  };

  // Use centralized logic for determining button disabled state
  // Recommendation card always adds exactly 1 item
  const isDisabled =
    isAdding || shouldAddToCartButtonBeDisabled(stock.eshop.maxOrderable, quantityInCart, 1, price);

  return (
    <Button
      type="button"
      {...preloadHandlers}
      onClick={(e) => {
        onAddProductToCart(e);
      }}
      loading={isAdding}
      disabled={isDisabled}
      variant="primary"
      className={clsxm('whitespace-nowrap', className, purchaseButtonClassName)}
    >
      <IconCartOutline className="size-4" />
      <span className="uppercase">
        {intl.formatMessage({
          id: 'component.client.product.productCardRecommendationButton.button.addToCart',
          defaultMessage: 'Do košíku',
        })}
      </span>
    </Button>
  );
}
